import {
  closingMonth,
  isValidIsoDay,
  periodMonthLabel,
  periodYearSchema,
  resultPeriod,
  formatDateTime,
  KPI_PERIODICITY_LABELS,
  KPI_RESULT_SOURCE_LABELS,
  kpiResultBatchResultSchema,
  kpiResultGridSchema,
  kpiResultHistorySchema,
  kpiResultSchema,
  targetOptionsSchema,
  targetPeriodLabel,
  type KpiPeriodicityName,
  type KpiResult,
  type KpiResultGridRow,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, FolderKanban, History, Lock, Paperclip, Plus, Trash2 } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ReportDeviationButton } from '@/features/alerts/ReportDeviation';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors } from '@/lib/forms';
import { parseValue } from '@/pages/MetasPage';

const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
const withUnit = (v: number, unit: string) =>
  unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
const pad = (n: number) => String(n).padStart(2, '0');
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/** Indicadores › Resultados (T-064c, RF-25, PRD §11.13): lançamento do realizado em grade ou individual. */
export function ResultadosPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const can = useCan();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-[90rem]">
      <PageHeader
        title="Resultados"
        description="Lance o realizado de cada indicador no período dele. A performance e o semáforo do mês são recalculados ao salvar; corrigir um valor guarda o anterior no histórico."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O realizado pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
            {project.status === 'ACTIVE' && can('alertas', 'create') ? (
              <ReportDeviationButton organizationId={organizationId} projectId={project.id} />
            ) : null}
          </div>
          {project.status !== 'ACTIVE' ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: os resultados ficam disponíveis só para consulta.
            </p>
          ) : null}
          <ResultsSection
            organizationId={organizationId}
            projectId={project.id}
            horizon={[project.horizonStart, project.horizonEnd]}
            readOnly={project.status !== 'ACTIVE'}
          />
        </div>
      )}
    </PageContainer>
  );
}

type Draft = { value: string; justification: string };

/** Filtro de semáforo do mês (a Visão Geral abre com ?semaforo= — T-140b). */
type LightFilter = '' | 'off' | 'red' | 'yellow';
const LIGHT_FILTERS: { value: LightFilter; label: string; param: string }[] = [
  { value: '', label: 'Todos', param: '' },
  { value: 'off', label: 'Fora da meta (amarelo ou vermelho)', param: 'fora-da-meta' },
  { value: 'red', label: 'Vermelho', param: 'vermelho' },
  { value: 'yellow', label: 'Amarelo', param: 'amarelo' },
];
const matchesLight = (filter: LightFilter, light: string | undefined) =>
  filter === '' || (filter === 'off' ? light === 'red' || light === 'yellow' : light === filter);

function ResultsSection({
  organizationId,
  projectId,
  horizon,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  horizon: [number, number];
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const can = useCan();
  const canEdit = !readOnly && can('metas-resultados', 'edit');
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/results`;
  const now = new Date();
  // ?ano=&mes=&semaforo= (link da Visão Geral — T-140b); sem eles, o mês atual dentro do horizonte.
  const [params] = useSearchParams();
  const askedYear = Number(params.get('ano'));
  const askedMonth = Number(params.get('mes'));
  const [year, setYear] = useState(
    Number.isInteger(askedYear) && askedYear >= horizon[0] && askedYear <= horizon[1]
      ? askedYear
      : Math.min(Math.max(now.getFullYear(), horizon[0]), horizon[1]),
  );
  const [month, setMonth] = useState(
    Number.isInteger(askedMonth) && askedMonth >= 1 && askedMonth <= 12
      ? askedMonth
      : year === now.getFullYear()
        ? now.getMonth() + 1
        : 1,
  );
  const [lightFilter, setLightFilter] = useState<LightFilter>(
    () => LIGHT_FILTERS.find((f) => f.param && f.param === params.get('semaforo'))?.value ?? '',
  );
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [needsJustification, setNeedsJustification] = useState<Record<string, string>>({});
  const [askReason, setAskReason] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [launching, setLaunching] = useState<KpiResultGridRow | null>(null);
  // Histórico de um resultado da linha: o geral ou o de uma unidade.
  const [historyOf, setHistoryOf] = useState<{ row: KpiResultGridRow; result: KpiResult } | null>(null);
  const [launchBusy, setLaunchBusy] = useState(false);
  const ids = { year: useId(), month: useId(), light: useId() };
  const years = Array.from({ length: horizon[1] - horizon[0] + 1 }, (_, i) => horizon[0] + i);

  const gridKey = ['results', projectId, 'grid', year, month];
  const grid = useQuery({
    queryKey: gridKey,
    queryFn: ({ signal }) =>
      client.request(`${base}/grid`, {
        query: { year: String(year), month: String(month) },
        schema: kpiResultGridSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const rows = grid.data?.rows ?? [];
  // Linha com lançamento não salvo nunca some pelo filtro (o que vai ao salvar fica à vista).
  const shownRows = rows.filter(
    (r) => matchesLight(lightFilter, r.month?.light) || drafts[r.kpi.id] !== undefined,
  );
  const gridEditable = (r: KpiResultGridRow) =>
    canEdit && r.editable && r.access === 'AREA' && r.period !== null;
  const dirty = Object.entries(drafts).filter(([kpiId, d]) => {
    const row = rows.find((r) => r.kpi.id === kpiId);
    if (!row) return false;
    const parsed = parseValue(d.value);
    return (
      parsed === 'invalid' ||
      parsed !== (row.result?.value ?? null) ||
      (d.justification.trim() || null) !== (row.result?.justification ?? null)
    );
  });
  const invalid = dirty.some(([, d]) => parseValue(d.value) === 'invalid');
  // Corrigir (mudar ou remover um realizado já lançado) pede motivo; em mês fechado, todo lançamento pede
  // justificativa (RN-06, T-066).
  const correcting = dirty.some(([kpiId]) => rows.find((r) => r.kpi.id === kpiId)?.result);
  const monthClosed = grid.data?.closed ?? false;
  const monthLabel = grid.data ? `${targetPeriodLabel('MONTH', grid.data.month)}/${grid.data.year}` : '';
  const needsReason = correcting || (monthClosed && dirty.length > 0);

  const reset = () => {
    setDrafts({});
    setNeedsJustification({});
    setSaveError(null);
  };
  // Trocar de período com lançamentos não salvos pede confirmação (o valor escolhido é lido na hora).
  const [pendingPeriod, setPendingPeriod] = useState<(() => void) | null>(null);
  const changePeriod = (fn: () => void) => {
    if (dirty.length) return setPendingPeriod(() => fn);
    reset();
    fn();
  };

  async function save(reason: string | null) {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    const items = dirty.map(([kpiId, d]) => {
      const row = rows.find((r) => r.kpi.id === kpiId)!;
      return {
        kpiId,
        referenceDate: row.result?.referenceDate ?? row.referenceDate!,
        businessUnitId: null,
        value: parseValue(d.value) as number | null,
        justification: d.justification.trim() || null,
      };
    });
    try {
      const result = await client.request(base, {
        method: 'PUT',
        body: { items, reason },
        schema: kpiResultBatchResultSchema,
      });
      setAskReason(false);
      await queryClient.invalidateQueries({ queryKey: ['results', projectId] });
      reset();
      const parts = [
        result.created ? `${result.created} novo${result.created === 1 ? '' : 's'}` : null,
        result.corrected ? `${result.corrected} corrigido${result.corrected === 1 ? '' : 's'}` : null,
        result.removed ? `${result.removed} removido${result.removed === 1 ? '' : 's'}` : null,
      ].filter(Boolean);
      toast.success('Realizado salvo', { description: parts.join(', ') || undefined });
    } catch (err) {
      const fields = apiFieldErrors(err);
      // O mês fica vermelho: a linha pede a justificativa do desvio (RN-26).
      const detail = err instanceof ApiError ? err.details : [];
      const red = detail.filter((d) => /^items\.\d+\.justification$/.test(d.path));
      if (red.length) {
        const next: Record<string, string> = {};
        for (const d of red) {
          const i = Number(d.path.split('.')[1]);
          next[items[i]!.kpiId] = d.message;
        }
        setNeedsJustification(next);
        setAskReason(false);
        setSaveError(new Error('Explique o desvio nas linhas destacadas e salve de novo.'));
      } else if (fields.reason) {
        setAskReason(true);
        setSaveError(new Error(err instanceof ApiError ? err.message : fields.reason));
        if (err instanceof ApiError && err.code === 'CLOSED_PERIOD') {
          void queryClient.invalidateQueries({ queryKey: gridKey });
        }
      } else {
        setSaveError(err);
        setAskReason(false);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:max-w-2xl sm:grid-cols-3">
        <div>
          <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
            Ano
          </label>
          <NativeSelect
            id={ids.year}
            value={String(year)}
            onChange={(e) => {
              const next = Number(e.target.value);
              changePeriod(() => setYear(next));
            }}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
            Mês
          </label>
          <NativeSelect
            id={ids.month}
            value={String(month)}
            onChange={(e) => {
              const next = Number(e.target.value);
              changePeriod(() => setMonth(next));
            }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {targetPeriodLabel('MONTH', m)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label htmlFor={ids.light} className="mb-1 block text-xs font-medium text-muted-foreground">
            Semáforo do mês
          </label>
          <NativeSelect
            id={ids.light}
            value={lightFilter}
            onChange={(e) => setLightFilter(e.target.value as LightFilter)}
          >
            {LIGHT_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      {monthClosed ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm"
        >
          <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            <span className="font-semibold">{monthLabel} está fechado.</span> Lançar ou corrigir o realizado
            deste mês pede justificativa e fica registrado na auditoria como período encerrado.
          </span>
        </p>
      ) : null}
      {grid.isPending ? (
        <LoadingState rows={4} label="Carregando os indicadores" />
      ) : grid.isError ? (
        <ErrorState error={grid.error} onRetry={() => void grid.refetch()} retrying={grid.isFetching} />
      ) : rows.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={ClipboardCheck}
          title="Nenhum indicador para lançar"
          description="Os indicadores do projeto que são das suas áreas (ou das suas unidades) aparecem aqui para receber o realizado."
          action={
            can('indicadores', 'view') ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}/indicadores/kpis`}>Ir para os KPIs</Link>
              </Button>
            ) : null
          }
        />
      ) : shownRows.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={ClipboardCheck}
          title="Nenhum indicador com esse semáforo no mês"
          description="Escolha outro semáforo (ou Todos) para ver os demais indicadores."
        />
      ) : (
        <div
          role="region"
          aria-label="Lançamentos do mês"
          tabIndex={0}
          className="relative overflow-x-auto rounded-lg border border-border"
        >
          <table
            className="w-full min-w-[46rem] border-collapse text-sm"
            aria-busy={grid.isFetching || saving}
          >
            <caption className="sr-only">
              Realizado de {targetPeriodLabel('MONTH', month)}/{year}
            </caption>
            <thead className="bg-muted/60 text-xs text-muted-foreground">
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-[1] w-36 min-w-36 bg-muted px-3 py-2 text-left font-semibold sm:w-auto sm:min-w-56"
                >
                  Indicador
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  Período
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  Realizado
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  Meta do mês
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  Performance do mês
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => {
                const draft = drafts[r.kpi.id];
                const editable = gridEditable(r);
                const shown = draft?.value ?? (r.result?.value != null ? formatValue(r.result.value) : '');
                const bad = draft !== undefined && parseValue(draft.value) === 'invalid';
                const red = needsJustification[r.kpi.id];
                const label = `Realizado de ${r.kpi.name}`;
                return (
                  <tr key={r.kpi.id} className="border-t border-border align-top">
                    <th
                      scope="row"
                      className="sticky left-0 z-[1] w-36 min-w-36 bg-surface px-3 py-2 text-left font-normal sm:w-auto sm:min-w-56"
                    >
                      <p className="font-semibold">{r.kpi.name}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-mono">{r.kpi.code}</span> · {r.kpi.unitLabel} ·{' '}
                        {KPI_PERIODICITY_LABELS[r.kpi.periodicity as KpiPeriodicityName]}
                      </p>
                      {r.access === 'UNIT' ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Você lança o realizado da sua unidade.
                        </p>
                      ) : null}
                    </th>
                    <td className="px-3 py-2 text-sm">
                      {r.period ? r.period.label : r.blockedReason ? '—' : `${r.entriesInMonth} no mês`}
                    </td>
                    <td className="px-3 py-2">
                      {editable ? (
                        <div className="flex flex-col items-end gap-1">
                          <input
                            aria-label={label}
                            aria-invalid={bad || undefined}
                            inputMode="decimal"
                            value={shown}
                            disabled={saving}
                            onChange={(e) =>
                              setDrafts((all) => ({
                                ...all,
                                [r.kpi.id]: {
                                  value: e.target.value,
                                  justification:
                                    all[r.kpi.id]?.justification ?? r.result?.justification ?? '',
                                },
                              }))
                            }
                            className={cn(
                              'h-9 w-32 rounded-md border border-border-strong bg-surface-raised px-2 text-right tabular-nums focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-ring/40 disabled:opacity-70',
                              draft && 'border-brand bg-brand-soft',
                              bad && 'border-status-red-solid',
                            )}
                          />
                          {red || r.result?.justification || draft?.justification ? (
                            <textarea
                              aria-label={`Justificativa de ${r.kpi.name}`}
                              aria-invalid={red ? true : undefined}
                              rows={2}
                              maxLength={2000}
                              disabled={saving}
                              value={draft?.justification ?? r.result?.justification ?? ''}
                              onChange={(e) =>
                                setDrafts((all) => ({
                                  ...all,
                                  [r.kpi.id]: {
                                    value:
                                      all[r.kpi.id]?.value ??
                                      (r.result?.value != null ? formatValue(r.result.value) : ''),
                                    justification: e.target.value,
                                  },
                                }))
                              }
                              className={cn(textareaClass, 'w-56 text-left')}
                            />
                          ) : null}
                          {red ? (
                            <p
                              role="alert"
                              className="max-w-56 text-right text-xs font-medium text-status-red-fg"
                            >
                              {red}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-right tabular-nums" aria-label={label}>
                          {r.result?.value != null ? withUnit(r.result.value, r.kpi.unitLabel) : '—'}
                        </p>
                      )}
                      {r.result?.evidence ? (
                        <a
                          href={r.result.evidence.url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 flex items-center justify-end gap-1 text-xs text-brand-strong underline-offset-2 hover:underline dark:text-brand"
                        >
                          <Paperclip className="size-3" aria-hidden />
                          {r.result.evidence.name}
                        </a>
                      ) : null}
                      {r.unitResults.length ? (
                        <ul
                          aria-label={`Realizado por unidade de ${r.kpi.name}`}
                          className="mt-1 flex flex-col items-end gap-0.5"
                        >
                          {r.unitResults.map((u) => (
                            <li key={u.id} className="flex items-center gap-1 text-xs">
                              <span className="text-muted-foreground">{u.businessUnit?.name}:</span>
                              <span className="tabular-nums">
                                {u.value === null ? 'removido' : withUnit(u.value, r.kpi.unitLabel)}
                              </span>
                              {u.evidence ? (
                                <Paperclip
                                  className="size-3 text-muted-foreground"
                                  aria-label="com evidência"
                                />
                              ) : null}
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="size-6"
                                aria-label={`Histórico de ${r.kpi.name} em ${u.businessUnit?.name ?? 'unidade'}`}
                                onClick={() => setHistoryOf({ row: r, result: u })}
                              >
                                <History aria-hidden />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.month?.target != null ? withUnit(r.month.target, r.kpi.unitLabel) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {r.month ? (
                        <StatusBadge
                          status={r.month.light as TrafficLight}
                          label={
                            r.month.performance !== null
                              ? `${formatValue(r.month.performance)}%`
                              : r.month.light === 'blue'
                                ? 'Futuro'
                                : 'Sem dados'
                          }
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Consolidado na área do indicador
                        </span>
                      )}
                      {r.blockedReason ? (
                        <p className="mt-1 text-xs text-muted-foreground">{r.blockedReason}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {canEdit && r.editable ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Lançar ${r.kpi.name} por data`}
                            onClick={() => setLaunching(r)}
                          >
                            <Plus aria-hidden />
                            Lançar
                          </Button>
                        ) : null}
                        {r.result ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Histórico de ${r.kpi.name}`}
                            onClick={() => setHistoryOf({ row: r, result: r.result! })}
                          >
                            <History aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && dirty.length ? (
        <div className="sticky bottom-3 z-10 flex flex-col gap-2 rounded-lg border border-border bg-surface-raised p-3 shadow-md sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm">
            {invalid
              ? 'Há valores inválidos (use números).'
              : `${dirty.length} ${dirty.length === 1 ? 'lançamento alterado' : 'lançamentos alterados'}${monthClosed ? ' — mês fechado, pede justificativa' : correcting ? ' — inclui correção' : ''}.`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} disabled={saving}>
              Descartar
            </Button>
            <Button
              loading={saving && !askReason}
              disabled={invalid}
              onClick={() => (needsReason ? setAskReason(true) : void save(null))}
            >
              Salvar lançamentos
            </Button>
          </div>
        </div>
      ) : null}
      {saveError && !askReason ? <FormAlert tone="error">{errorMessage(saveError)}</FormAlert> : null}

      <ConfirmDialog
        open={pendingPeriod !== null}
        onOpenChange={(open) => !open && setPendingPeriod(null)}
        title="Descartar os lançamentos não salvos?"
        description="Há valores digitados nesta grade que ainda não foram salvos. Trocar de ano ou mês descarta esses lançamentos."
        confirmLabel="Descartar e trocar"
        destructive
        onConfirm={() => {
          reset();
          pendingPeriod?.();
          setPendingPeriod(null);
        }}
      />
      <Dialog
        open={askReason}
        onOpenChange={(o) => !o && setAskReason(false)}
        busy={saving}
        title={monthClosed ? `Justificativa: ${monthLabel} fechado` : 'Motivo da correção'}
      >
        {askReason ? (
          <ReasonForm
            closedLabel={monthClosed ? monthLabel : null}
            saving={saving}
            error={saveError}
            onCancel={() => setAskReason(false)}
            onSubmit={(r) => void save(r)}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={launching !== null}
        onOpenChange={(o) => !o && setLaunching(null)}
        title="Lançar o realizado"
        busy={launchBusy}
        className="w-[min(36rem,calc(100vw-2rem))]"
      >
        {launching ? (
          <LaunchForm
            client={client}
            organizationId={organizationId}
            base={base}
            targetsBase={`/organizations/${organizationId}/projects/${projectId}/targets`}
            projectId={projectId}
            row={launching}
            onBusyChange={setLaunchBusy}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['results', projectId] })}
            onDone={async () => {
              setLaunching(null);
              await queryClient.invalidateQueries({ queryKey: ['results', projectId] });
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={historyOf !== null}
        onOpenChange={(o) => !o && setHistoryOf(null)}
        title="Histórico do realizado"
      >
        {historyOf ? (
          <ResultHistory
            client={client}
            base={base}
            projectId={projectId}
            row={historyOf.row}
            result={historyOf.result}
            canEdit={canEdit}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ['results', projectId] })}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function ReasonForm({
  closedLabel,
  saving,
  error,
  onSubmit,
  onCancel,
}: {
  /** Mês fechado (T-066): o texto explica o período encerrado em vez da correção. */
  closedLabel: string | null;
  saving: boolean;
  error: unknown;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [local, setLocal] = useState<string | null>(null);
  const id = useId();
  function submit(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3)
      return setLocal(
        closedLabel
          ? 'Explique por que o mês fechado está sendo alterado.'
          : 'Explique o motivo da correção.',
      );
    setLocal(null);
    onSubmit(reason.trim());
  }
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {closedLabel
          ? `${closedLabel} está fechado: o lançamento fica registrado na auditoria como período encerrado, junto com esta justificativa.`
          : 'Você está corrigindo um realizado já lançado. A versão anterior fica no histórico, junto com este motivo.'}
      </p>
      <FormField id={id} label={closedLabel ? 'Justificativa' : 'Motivo'} error={local ?? undefined}>
        <textarea
          {...fieldA11y(id, local ?? undefined)}
          value={reason}
          maxLength={1000}
          rows={3}
          onChange={(e) => setReason(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {closedLabel ? 'Salvar com justificativa' : 'Salvar com motivo'}
        </Button>
      </div>
    </form>
  );
}

type Client = ReturnType<typeof useAuth>['client'];

/** Lançamento individual: data de referência, unidade (opcional), valor, justificativa e evidência. */
function LaunchForm({
  client,
  organizationId,
  base,
  targetsBase,
  projectId,
  row,
  onDone,
  onSaved,
  onBusyChange,
}: {
  client: Client;
  organizationId: string;
  base: string;
  targetsBase: string;
  projectId: string;
  row: KpiResultGridRow;
  onDone: () => Promise<void>;
  /** O valor foi gravado (a grade pode atualizar mesmo que a evidência falhe depois). */
  onSaved: () => Promise<unknown>;
  onBusyChange: (busy: boolean) => void;
}) {
  // Resultado já gravado quando só a evidência falhou: tentar de novo só reenvia o arquivo.
  const [savedId, setSavedId] = useState<string | null>(null);
  const [referenceDate, setReferenceDate] = useState(row.referenceDate ?? todayIso());
  const [unitId, setUnitId] = useState('');
  const [value, setValue] = useState('');
  const [justification, setJustification] = useState('');
  const [reason, setReason] = useState('');
  const [needReason, setNeedReason] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { date: useId(), unit: useId(), value: useId(), just: useId(), reason: useId(), file: useId() };
  // Unidades do escopo (as mesmas das metas por unidade).
  const units = useQuery({
    queryKey: ['targets', projectId, 'options'],
    queryFn: ({ signal }) =>
      client.request(`${targetsBase}/options`, { schema: targetOptionsSchema, signal }),
  });
  const unitRequired = row.access === 'UNIT';
  // Período encerrado (RN-06, T-066): o mês em que o período da data fecha está fechado? Pede a
  // justificativa antes de enviar.
  const closing =
    referenceDate && isValidIsoDay(referenceDate)
      ? closingMonth(
          resultPeriod(
            {
              periodicity: row.kpi.periodicity as KpiPeriodicityName,
              validFrom: row.kpi.validFrom,
              customPeriodDays: row.kpi.customPeriodDays,
            },
            referenceDate,
          ).end,
        )
      : null;
  const periods = useQuery({
    queryKey: ['periods', organizationId, closing?.year],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/periods`, {
        query: { year: String(closing!.year) },
        schema: periodYearSchema,
        signal,
      }),
    enabled: closing !== null,
  });
  const closedHere =
    closing !== null && periods.data?.months.find((m) => m.month === closing.month)?.status === 'CLOSED';
  const askReason = needReason || closedHere;

  async function uploadEvidence(id: string) {
    const form = new FormData();
    form.append('file', file!);
    await client.request(`${base}/${id}/evidence`, { method: 'PUT', body: form, schema: kpiResultSchema });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (savedId) {
      // O valor já está salvo: só reenvia a evidência.
      if (!file) return setErrors({ file: 'Escolha o arquivo da evidência.' });
      setSaving(true);
      onBusyChange(true);
      setFormError(null);
      try {
        await uploadEvidence(savedId);
        toast.success('Evidência anexada');
        await onDone();
      } catch (err) {
        setFormError(err);
      } finally {
        setSaving(false);
        onBusyChange(false);
      }
      return;
    }
    const next: Record<string, string> = {};
    const parsed = parseValue(value);
    if (parsed === null || parsed === 'invalid') next.value = 'Informe o realizado em números.';
    if (!referenceDate) next.referenceDate = 'Informe a data de referência.';
    else if (referenceDate > todayIso()) next.referenceDate = 'O realizado não pode ser de uma data futura.';
    if (unitRequired && !unitId) next.businessUnitId = 'Escolha a sua unidade.';
    if (askReason && reason.trim().length < 3) {
      next.reason = closedHere ? 'Explique por que o mês fechado está sendo alterado.' : 'Explique o motivo.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    onBusyChange(true);
    setFormError(null);
    try {
      const saved = await client.request(base, {
        method: 'PUT',
        body: {
          items: [
            {
              kpiId: row.kpi.id,
              referenceDate,
              businessUnitId: unitId || null,
              value: parsed as number,
              justification: justification.trim() || null,
            },
          ],
          reason: askReason ? reason.trim() : null,
        },
        schema: kpiResultBatchResultSchema,
      });
      const item = saved.items[0];
      await onSaved();
      if (file && item) {
        try {
          await uploadEvidence(item.id);
        } catch (err) {
          setSavedId(item.id);
          setFormError(
            new Error(
              `O realizado foi salvo, mas a evidência não foi anexada (${errorMessage(err)}). Escolha o arquivo e tente de novo.`,
            ),
          );
          return;
        }
      }
      toast.success('Realizado lançado', {
        description: file ? 'Com a evidência anexada.' : undefined,
      });
      await onDone();
    } catch (err) {
      const fields = apiFieldErrors(err);
      const detail = err instanceof ApiError ? err.details : [];
      const just = detail.find((d) => d.path.endsWith('.justification'));
      if (fields.reason) {
        setNeedReason(true);
        const closed = err instanceof ApiError && err.code === 'CLOSED_PERIOD';
        // O mês fechou depois de abrir o formulário: a lista de meses fechados é lida de novo.
        if (closed) void periods.refetch();
        setErrors({
          reason: closed ? err.message : 'Já há realizado neste período: explique o motivo da correção.',
        });
      } else if (just) {
        setErrors({ justification: just.message });
      } else {
        setFormError(err);
      }
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm">
        <span className="font-semibold">{row.kpi.name}</span>{' '}
        <span className="text-muted-foreground">
          · {KPI_PERIODICITY_LABELS[row.kpi.periodicity as KpiPeriodicityName]} · {row.kpi.unitLabel}
        </span>
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.date} label="Data de referência" error={errors.referenceDate}>
          <Input
            {...fieldA11y(ids.date, errors.referenceDate)}
            type="date"
            value={referenceDate}
            max={todayIso()}
            onChange={(e) => setReferenceDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.value} label={`Realizado (${row.kpi.unitLabel})`} error={errors.value}>
          <Input
            {...fieldA11y(ids.value, errors.value)}
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField
          id={ids.unit}
          label={unitRequired ? 'Unidade' : 'Unidade (opcional)'}
          error={errors.businessUnitId}
        >
          <NativeSelect
            {...fieldA11y(ids.unit, errors.businessUnitId)}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">{unitRequired ? 'Escolha a unidade' : 'Geral (sem unidade)'}</option>
            {units.isPending ? <option disabled>Carregando as unidades…</option> : null}
            {units.data?.businessUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        {units.isError ? (
          <div className="sm:col-span-2">
            <FormAlert tone="error">
              <span className="flex flex-wrap items-center gap-2">
                Não foi possível carregar as unidades: {errorMessage(units.error)}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={units.isFetching}
                  onClick={() => void units.refetch()}
                >
                  Tentar de novo
                </Button>
              </span>
            </FormAlert>
          </div>
        ) : null}
        <FormField id={ids.file} label="Evidência (opcional)" error={errors.file}>
          <Input
            id={ids.file}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.docx,.doc,.txt"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="h-11 py-2"
          />
        </FormField>
      </div>
      <FormField
        id={ids.just}
        label="Justificativa (obrigatória quando o mês fica vermelho)"
        error={errors.justification}
      >
        <textarea
          {...fieldA11y(ids.just, errors.justification)}
          value={justification}
          rows={2}
          maxLength={2000}
          onChange={(e) => setJustification(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {closedHere && closing ? (
        <FormAlert tone="locked">
          {periodMonthLabel(closing.year, closing.month)} está fechado: o lançamento pede justificativa e fica
          registrado na auditoria como período encerrado.
        </FormAlert>
      ) : null}
      {askReason ? (
        <FormField id={ids.reason} label="Justificativa" error={errors.reason}>
          <textarea
            {...fieldA11y(ids.reason, errors.reason)}
            value={reason}
            rows={2}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            className={textareaClass}
          />
        </FormField>
      ) : null}
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="submit" loading={saving}>
          {savedId ? 'Anexar a evidência' : 'Lançar'}
        </Button>
      </div>
    </form>
  );
}

/** Versões do realizado do período, com a evidência de cada uma; anexar ou remover a evidência atual. */
function ResultHistory({
  client,
  base,
  projectId,
  row,
  result: opened,
  canEdit,
  onChanged,
}: {
  client: Client;
  base: string;
  projectId: string;
  row: KpiResultGridRow;
  result: KpiResult;
  canEdit: boolean;
  onChanged: () => Promise<unknown>;
}) {
  // Cada anexo/remoção cria uma versão nova: as próximas ações valem para a versão que voltou.
  const [result, setResult] = useState(opened);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fileId = useId();
  const history = useQuery({
    queryKey: [
      'results',
      projectId,
      'history',
      row.kpi.id,
      result.periodStart,
      result.businessUnit?.id ?? '',
      result.version,
    ],
    queryFn: ({ signal }) =>
      client.request(`${base}/history`, {
        query: {
          kpiId: row.kpi.id,
          periodStart: result.periodStart,
          businessUnitId: result.businessUnit?.id,
        },
        schema: kpiResultHistorySchema,
        signal,
      }),
  });
  async function evidence(file: File | null) {
    setBusy(true);
    setError(null);
    try {
      if (file) {
        const form = new FormData();
        form.append('file', file);
        setResult(
          await client.request(`${base}/${result.id}/evidence`, {
            method: 'PUT',
            body: form,
            schema: kpiResultSchema,
          }),
        );
        toast.success('Evidência anexada');
      } else {
        setResult(
          await client.request(`${base}/${result.id}/evidence`, {
            method: 'DELETE',
            schema: kpiResultSchema,
          }),
        );
        toast.success('Evidência removida');
      }
      await onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        <span className="font-semibold">{row.kpi.name}</span> — {result.periodLabel}
        {result.businessUnit ? ` · ${result.businessUnit.name}` : ''}
      </p>
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={fileId} className="text-sm font-medium">
            {result.evidence ? 'Trocar a evidência' : 'Anexar evidência'}
          </label>
          <Input
            id={fileId}
            type="file"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void evidence(f);
            }}
            className="h-10 max-w-64 py-1.5"
          />
          {result.evidence ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => void evidence(null)}>
              <Trash2 aria-hidden />
              Remover evidência
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      {history.isPending ? (
        <LoadingState rows={2} label="Carregando o histórico" />
      ) : history.isError ? (
        <ErrorState
          error={history.error}
          onRetry={() => void history.refetch()}
          retrying={history.isFetching}
        />
      ) : (
        <ol aria-label="Versões do realizado" className="flex flex-col gap-2">
          {history.data.items.map((v) => (
            <li key={v.id} className="rounded-md border border-border bg-surface p-3 text-sm">
              <p className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">
                  Versão {v.version}
                  {v.current ? ' (vigente)' : ''}
                </span>
                <span className="tabular-nums">
                  {v.value === null ? 'Removido' : withUnit(v.value, row.kpi.unitLabel)}
                </span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {v.createdByName} · {formatDateTime(v.createdAt)} · {KPI_RESULT_SOURCE_LABELS[v.source]}
              </p>
              {v.reason ? <p className="mt-1 text-xs">Motivo: {v.reason}</p> : null}
              {v.justification ? <p className="mt-1 text-xs">Justificativa: {v.justification}</p> : null}
              {v.evidence ? (
                <a
                  href={v.evidence.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-xs text-brand-strong underline-offset-2 hover:underline dark:text-brand"
                >
                  <Paperclip className="size-3" aria-hidden />
                  {v.evidence.name}
                </a>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
